import {
  type APIConstraintEnforcementMode,
  BooleanOperator,
  type QueryBuilderQuery,
} from '@ardoq/api-types';
import {
  advancedSearchComponentNameAndTypeAndStartSetQuery,
  queryBuilderOperations,
} from '@ardoq/query-builder';
import { uniq } from 'lodash';
import type { TripleWithContext } from 'createReferences/toTriplesWithContext';
import { isCreatableTriple } from 'createReferences/isCreatableTriple';
import { isAllowedByEnforcementRule } from 'createReferences/getTripleConstraintsStatus';
import { getLinkedType } from 'traversals/pathAndGraphUtils';

export const MIN_FILTER_TERM_LENGTH = 3;

export const toQueriedTerm = (filterTerm: string): string | null => {
  const trimmedTerm = filterTerm.trim();
  if (trimmedTerm.length < MIN_FILTER_TERM_LENGTH) return null;
  return trimmedTerm.slice(0, MIN_FILTER_TERM_LENGTH).toLowerCase();
};

export const toTargetTypeNames = (
  triples: TripleWithContext[],
  constraintsEnforcementMode: APIConstraintEnforcementMode
): string[] =>
  uniq(
    triples
      .filter(triple =>
        isAllowedByEnforcementRule(triple, constraintsEnforcementMode)
      )
      .filter(isCreatableTriple)
      .map(getLinkedType)
  ).sort();

export const toComponentQuery = (
  queriedTerm: string,
  targetTypeNames: string[]
): QueryBuilderQuery =>
  advancedSearchComponentNameAndTypeAndStartSetQuery(queriedTerm, '', {
    condition: BooleanOperator.OR,
    rules: targetTypeNames.map(
      queryBuilderOperations.getComponentTypeQueryRule
    ),
  });

export type NoodleSearchState =
  | { kind: 'loadingTargetTypes' }
  | { kind: 'noTargetTypes' }
  | { kind: 'termTooShort' }
  | { kind: 'searching'; query: QueryBuilderQuery };

export const toNoodleSearchState = (
  filterTerm: string,
  targetTypeNames: string[] | null
): NoodleSearchState => {
  if (targetTypeNames === null) return { kind: 'loadingTargetTypes' };
  if (targetTypeNames.length === 0) return { kind: 'noTargetTypes' };
  const queriedTerm = toQueriedTerm(filterTerm);
  if (queriedTerm === null) return { kind: 'termTooShort' };
  return {
    kind: 'searching',
    query: toComponentQuery(queriedTerm, targetTypeNames),
  };
};
